import { PRECISION } from './constants';
import { Hole, HoleLocation } from './hole';
import { Part, SVG_NS, ViewContext } from './part';

export type FlangeSide = 'ds' | 'nds';

export interface FlangeProps {
  pcd: number;
  numHoles: number;
  rotation: number;
  spokeHoleDiameter: number;
  side: FlangeSide;
  centerOffset: number;
}

export class Flange extends Part {
  private holes: Hole[];

  constructor(
    public id: string,
    private props: FlangeProps
  ) {
    super();
    this.holes = this.generateHoles();
  }

  get children(): Hole[] {
    return this.holes;
  }

  update(props: Partial<FlangeProps>): void {
    this.props = {
      ...this.props,
      ...props,
    };
    const newHoles = this.generateHoles();
    const n = Math.max(newHoles.length, this.holes.length);
    for (let i = 0; i < n; i++) {
      const oldHole = this.holes[i];
      const newHole = newHoles[i];
      if (oldHole && newHole) {
        oldHole.update(newHole.props);
      } else if (!oldHole && newHole) {
        this.holes[i] = newHole;
      } else if (oldHole && !newHole) {
        this.holes = this.holes.slice(0, i);
        break;
      }
    }
  }

  private generateHoles() {
    const holes: Hole[] = [];
    const angleStep = 360 / this.props.numHoles;
    const initAngle = angleStep * this.props.rotation;
    for (let i = 0; i < this.props.numHoles; i++) {
      const angle = initAngle + angleStep * i;
      let location: HoleLocation = this.props.side === 'ds' ? 'flange-ds' : 'flange-nds';
      const idPrefix = this.props.side === 'ds' ? 'S' : 'N';
      holes.push(
        new Hole(`${idPrefix}${i + 1}`, {
          angle,
          distance: this.props.pcd / 2,
          diameter: this.props.spokeHoleDiameter,
          location,
          centerOffset: this.props.centerOffset,
        })
      );
    }
    return holes;
  }

  render(viewCtx: ViewContext): SVGElement {
    const s = viewCtx.scaler;
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('flange');
    group.setAttribute('id', this.id);
    const sampleHole = this.children[0];
    const holeDiameter = (sampleHole as Hole).schematicDiameter(viewCtx);
    const schematicPcd =
      viewCtx.viewSide === this.props.side
        ? s.schematicNearFlangeDiameter
        : s.schematicNearFlangeDiameter + holeDiameter * 3;
    const flangeDiameter = viewCtx.schematicView
      ? schematicPcd + holeDiameter * 2
      : s.pxPerMm * (this.props.pcd + this.props.spokeHoleDiameter * 2);
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.classList.add(this.props.side == viewCtx.viewSide ? 'near-flange' : 'far-flange');
    circle.classList.add('select-resetting');
    circle.setAttribute('cx', '0');
    circle.setAttribute('cy', '0');
    circle.setAttribute('r', (flangeDiameter / 2).toFixed(PRECISION));
    group.appendChild(circle);
    const axleDiameter = viewCtx.schematicView
      ? viewCtx.scaler.schematicAxleDiameter
      : 10 * s.pxPerMm;
    const axle = document.createElementNS(SVG_NS, 'circle');
    axle.classList.add('axle-hole', 'select-resetting');
    axle.setAttribute('cx', '0');
    axle.setAttribute('cy', '0');
    axle.setAttribute('r', (axleDiameter / 2).toFixed(PRECISION));
    group.appendChild(axle);
    if (viewCtx.viewSide === 'ds' && viewCtx.schematicView) {
      const firstHole = this.children[0];
      const startMarker = document.createElementNS(SVG_NS, 'circle');
      startMarker.classList.add('flange-start-marker');
      const [x, y] = firstHole.xy(viewCtx);
      startMarker.setAttribute('cx', (x * 0.75).toFixed(PRECISION));
      startMarker.setAttribute('cy', (y * 0.75).toFixed(PRECISION));
      startMarker.setAttribute('r', '1.5');
      group.appendChild(startMarker);
    }
    this.children.forEach((child) => {
      group.appendChild(child.render(viewCtx));
    });
    return group;
  }
}
